import { useEffect, useRef, useState, type HTMLAttributes, type ReactNode, type TdHTMLAttributes, type ThHTMLAttributes } from 'react'

/** Data-table primitives. Headers are small tracked caps; numbers should use `numeric`.
 * When a table is wider than its container (common on mobile), a soft edge fade hints that
 * there's more to scroll to — shown only while there actually is, on the side that has it. */
export function Table({ children, className = '', dimmed = false, ...props }: HTMLAttributes<HTMLTableElement> & { dimmed?: boolean }) {
  const scrollRef = useRef<HTMLDivElement>(null)
  const [edge, setEdge] = useState<{ start: boolean; end: boolean }>({ start: false, end: false })

  useEffect(() => {
    const el = scrollRef.current
    if (!el) return
    const update = () => {
      setEdge({
        start: el.scrollLeft > 2,
        end: el.scrollWidth - el.clientWidth - el.scrollLeft > 2,
      })
    }
    update()
    el.addEventListener('scroll', update, { passive: true })
    const ro = new ResizeObserver(update)
    ro.observe(el)
    return () => {
      el.removeEventListener('scroll', update)
      ro.disconnect()
    }
  }, [children])

  return (
    <div className={`relative transition-opacity duration-300 ${dimmed ? 'opacity-50' : ''}`}>
      <div ref={scrollRef} className="overflow-x-auto">
        <table className={`w-full border-collapse text-[13.5px] ${className}`} {...props}>
          {children}
        </table>
      </div>
      <div
        aria-hidden="true"
        className={`pointer-events-none absolute inset-y-0 left-0 w-8 bg-gradient-to-r from-surface to-transparent transition-opacity duration-200 ${edge.start ? 'opacity-100' : 'opacity-0'}`}
      />
      <div
        aria-hidden="true"
        className={`pointer-events-none absolute inset-y-0 right-0 w-8 bg-gradient-to-l from-surface to-transparent transition-opacity duration-200 ${edge.end ? 'opacity-100' : 'opacity-0'}`}
      />
    </div>
  )
}

export function THead({ children }: { children: ReactNode }) {
  return <thead className="border-b border-ink-950/[0.06]">{children}</thead>
}

export function Th({ children, className = '', align = 'left', ...props }: ThHTMLAttributes<HTMLTableCellElement> & { align?: 'left' | 'right' }) {
  return (
    <th
      scope="col"
      className={`whitespace-nowrap px-5 py-2.5 text-[12px] font-medium uppercase tracking-[0.1em] text-ink-600 first:pl-5 ${align === 'right' ? 'text-right' : 'text-left'} ${className}`}
      {...props}
    >
      {children}
    </th>
  )
}

export function TBody({ children }: { children: ReactNode }) {
  return <tbody className="divide-y divide-ink-950/[0.05]">{children}</tbody>
}

export function Tr({ children, className = '', ...props }: HTMLAttributes<HTMLTableRowElement>) {
  return (
    <tr className={`group transition-colors duration-150 hover:bg-ink-950/[0.02] ${className}`} {...props}>
      {children}
    </tr>
  )
}

export function Td({
  children,
  className = '',
  numeric = false,
  muted = false,
  align = 'left',
  ...props
}: TdHTMLAttributes<HTMLTableCellElement> & { numeric?: boolean; muted?: boolean; align?: 'left' | 'right' }) {
  return (
    <td
      className={`whitespace-nowrap px-5 py-3 align-middle ${numeric ? 'tabular' : ''} ${muted ? 'text-ink-500' : 'text-ink-900'} ${align === 'right' ? 'text-right' : ''} ${className}`}
      {...props}
    >
      {children}
    </td>
  )
}

/** Two-letter monogram in a rounded square — for people rows. */
export function Monogram({ name, size = 'md' }: { name: string; size?: 'sm' | 'md' }) {
  const initials = name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((p) => p[0]?.toUpperCase() ?? '')
    .join('')
  // A stable, low-chroma hue derived from the name so rows are distinguishable without shouting.
  let h = 0
  for (const ch of name) h = (h * 31 + ch.charCodeAt(0)) % 360
  const dims = size === 'sm' ? 'size-6 text-[12px] rounded-[7px]' : 'size-8 text-[11.5px] rounded-[9px]'
  return (
    <span
      aria-hidden="true"
      className={`inline-grid shrink-0 place-items-center font-semibold tracking-wide ${dims}`}
      style={{ background: `oklch(0.94 0.035 ${h})`, color: `oklch(0.4 0.09 ${h})`, boxShadow: `inset 0 0 0 1px oklch(0.4 0.09 ${h} / 0.12)` }}
    >
      {initials || '·'}
    </span>
  )
}
